import React, { useCallback, useEffect, useState } from "react";
import axiosConfig from "../Service/axiosConfig";

const JSON_HEADERS = { headers: { "Content-Type": "application/json" } };

const RELATION_LABELS = {
    father: "Father",
    mother: "Mother",
    legal_guardian: "Legal guardian",
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "--");

/** Staff view of a member's terms, contact-sharing and parent/guardian consent (DPDP record). */
const ProfileConsent = ({ ProfileData }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [resetting, setResetting] = useState(false);

    const load = useCallback(async () => {
        if (!ProfileData?.id) return;
        setLoading(true);
        setError("");
        try {
            const res = await axiosConfig.get("/accounts/api/consent-history/", {
                params: { profile_id: ProfileData.id },
            });
            setData(res.data);
        } catch (err) {
            setError(err?.response?.data?.error || "Could not load consent history");
        } finally {
            setLoading(false);
        }
    }, [ProfileData?.id]);

    useEffect(() => {
        load();
    }, [load]);

    const resetGuardian = async () => {
        if (!window.confirm("Reset parent verification? The member will be asked to verify a parent again and, while enforcement is on, will be hidden until then.")) {
            return;
        }
        setResetting(true);
        try {
            await axiosConfig.post("/accounts/api/guardian/staff-reset/", { profile_id: ProfileData.id }, JSON_HEADERS);
            await load();
        } catch (err) {
            setError(err?.response?.data?.error || "Could not reset");
        } finally {
            setResetting(false);
        }
    };

    if (loading && !data) return <p className="p-3">Loading…</p>;
    if (error && !data) return <div className="alert alert-danger m-3">{error}</div>;
    if (!data) return null;

    const guardian = data.guardian_record;
    const status = !data.is_minor
        ? "Adult"
        : data.guardian_verified
            ? "Under 18 · parent verified"
            : "Under 18 · parent NOT verified";

    return (
        <div className="p-3">
            {error && <div className="alert alert-danger">{error}</div>}
            <table className="table table-bordered">
                <tbody>
                    <tr>
                        <th style={{ width: "30%" }}>Age status</th>
                        <td>
                            <span className={`badge ${data.is_minor && !data.guardian_verified ? "bg-danger" : data.is_minor ? "bg-warning text-dark" : "bg-success"}`}>
                                {status}
                            </span>
                            {data.is_minor && !data.profile_visible && (
                                <span className="ms-2 text-danger">Hidden from other members</span>
                            )}
                        </td>
                    </tr>
                    <tr>
                        <th>Terms version</th>
                        <td>{data.terms_version || "--"} {data.terms_current ? "(current)" : "(outdated)"}</td>
                    </tr>
                    <tr>
                        <th>Show number to casting</th>
                        <td>{data.show_contact_to_casting ? "Yes" : "No (hidden by member)"}</td>
                    </tr>
                    {guardian && (
                        <>
                            <tr>
                                <th>Parent / guardian</th>
                                <td>
                                    {guardian.guardian_name} ({RELATION_LABELS[guardian.relation] || guardian.relation}) · {guardian.guardian_mobile}
                                </td>
                            </tr>
                            <tr>
                                <th>Verified at</th>
                                <td>{formatDate(guardian.verified_at)}</td>
                            </tr>
                            <tr>
                                <th>Withdrawn at</th>
                                <td>{formatDate(guardian.withdrawn_at)}</td>
                            </tr>
                            <tr>
                                <th>Consent version / source / IP</th>
                                <td>{guardian.consent_version || "--"} / {guardian.source || "--"} / {guardian.consent_ip || "--"}</td>
                            </tr>
                        </>
                    )}
                </tbody>
            </table>

            {data.is_minor && data.guardian_verified && (
                <button type="button" className="btn btn-outline-danger btn-sm mb-3" disabled={resetting} onClick={resetGuardian}>
                    {resetting ? "Resetting…" : "Reset parent verification"}
                </button>
            )}

            <h6 className="mt-2">Consent history</h6>
            {data.events?.length ? (
                <table className="table table-sm table-striped">
                    <thead>
                        <tr>
                            <th>When</th>
                            <th>Kind</th>
                            <th>Action</th>
                            <th>Version</th>
                            <th>Source</th>
                            <th>IP</th>
                            <th>Detail</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.events.map((event, index) => (
                            <tr key={`${event.created_at}-${index}`}>
                                <td>{formatDate(event.created_at)}</td>
                                <td>{event.kind}</td>
                                <td>{event.action}</td>
                                <td>{event.version || "--"}</td>
                                <td>{event.source || "--"}</td>
                                <td>{event.ip_address || "--"}</td>
                                <td><code>{event.detail && Object.keys(event.detail).length ? JSON.stringify(event.detail) : ""}</code></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <p className="text-muted">No consent events recorded. Accounts created before October 2026 are marked "legacy".</p>
            )}
        </div>
    );
};

export { ProfileConsent };
